// tslint:disable: no-magic-numbers max-file-line-count
import { _throw } from 'rxjs/observable/throw';
import { catchError, concatMap, tap, finalize } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { get as getValue } from 'lodash';
import { empty } from 'rxjs/observable/empty';
import * as momentModule from 'moment';
import { TRAVEL_AGENT_CONSTANTS } from './travel-agent.constants';
import { TravelAgentRestService } from './rest/travel-agent-rest.service';
import { WindowRef } from '../window-ref/window-ref.service';
var TravelAgentService = /** @class */ (function () {
    function TravelAgentService(cookieService, travelAgentRestService, windowRef) {
        this.cookieService = cookieService;
        this.travelAgentRestService = travelAgentRestService;
        this.windowRef = windowRef;
        // TODO: flowEnabled property should be removed when travel agent toggle removed (enabled as default)
        this.flowEnabled = false;
        this.constants = TRAVEL_AGENT_CONSTANTS;
        this.moment = momentModule;
    }
    /**
     * Set if travel agent flow is enabled based on toggle value
     * @param toggleValue travel agent feature toggle value
     */
    TravelAgentService.prototype.setFlowEnabled = function (toggleValue) {
        this.flowEnabled = toggleValue;
    };
    /**
     * Set expiration times variables for session related and TA related cookies
     * @param expiresIn auth token expiration time
     */
    TravelAgentService.prototype.setCookiesExpirationTimes = function (expiresIn) {
        this.sessionExpirationDate = this.moment().add(parseInt(expiresIn, 10) || this.constants.SESSION_HOUR_SEC, 'seconds').toDate();
        this.oneDayExpirationDate = this.moment().add(this.constants.DEFAULT_DAY_EXPIRATION_MIN, 'minutes').toDate();
    };
    /**
     * Set webapi base url for rest service
     * @param baseUrl
     */
    TravelAgentService.prototype.setRestBaseUrl = function (baseUrl) {
        this.travelAgentRestService.setBaseUrl(baseUrl);
    };
    /**
     * Validate if a Travel Agent user is logged in
     * @returns Validation result
     */
    TravelAgentService.prototype.isTravelAgentLoggedIn = function () {
        return this.flowEnabled && !!this.cookieService.get(this.constants.COOKIES.TA_INFO);
    };
    /**
     * Returns agency id for a logged in Travel Agent user
     * @returns Agency id value
     */
    TravelAgentService.prototype.getTravelAgencyId = function () {
        return this.cookieService.get(this.constants.COOKIES.AGENCY_ID);
    };
    /**
     * Returns auth token for a logged in Travel Agent user, to be used in Authorization header
     * @returns Value for Authorization header
     */
    TravelAgentService.prototype.getTravelAgentAuthToken = function () {
        return this.travelAgentRestService.getAuthHeader();
    };
    /**
     * Returns AgencyUUID based on agencyId and Conversation_UUID cookies
     * @returns AgencyUUID value for the session
     */
    TravelAgentService.prototype.getAgencyUUID = function () {
        var agencyId = this.getTravelAgencyId();
        var conversationUUID = this.cookieService.get(this.constants.COOKIES.CONVERSATION_UUID);
        return agencyId && conversationUUID ? conversationUUID + "_" + agencyId : '';
    };
    /**
     * Removes a Travel Agent related cookie with
     * regular domain
     * starting with dot domain
     * base level domain. Example: go.com, disney.go.com
     * @param cookieName
     */
    TravelAgentService.prototype.removeCookie = function (cookieName) {
        var _a = this.constants.COOKIES.COOKIE_OPTIONS, path = _a.path, domain = _a.domain, secondLevelDomain = _a.secondLevelDomain, thirdLevelDomain = _a.thirdLevelDomain;
        this.cookieService.delete(cookieName, path);
        this.cookieService.delete(cookieName, path, domain);
        this.cookieService.delete(cookieName, path, secondLevelDomain);
        this.cookieService.delete(cookieName, path, thirdLevelDomain);
    };
    /**
     * Stores auth token based cookies
     * @param data auth token response
     * @param { string } data.access_token auth token (B2B)
     * @param { string } data.expires_in auth token expiration time
     */
    TravelAgentService.prototype.storeAuthToken = function (_a) {
        var access_token = _a.access_token, expires_in = _a.expires_in;
        var cookieConstants = this.constants.COOKIES;
        var storageConstants = this.constants.STORAGE;
        var _b = cookieConstants.COOKIE_OPTIONS, path = _b.path, domain = _b.domain;
        var expires = this.moment().add(this.constants.SESSION_MIN, 'minutes').toDate();
        var ttlString = String(expires.getTime());
        var currentTime = String(new Date().valueOf());
        this.setCookiesExpirationTimes(expires_in);
        this.removeCookie(cookieConstants.SWID);
        this.removeCookie(cookieConstants.TOKEN);
        this.removeCookie(cookieConstants.TOKEN_EXPIRATION);
        this.removeCookie(cookieConstants.TOKEN_START_TIME);
        this.removeCookie(cookieConstants.TOKEN_REFRESH);
        this.removeCookie(cookieConstants.TOKEN_REFRESH_PP);
        this.removeCookie(cookieConstants.GUEST_INFO);
        this.removeCookie(cookieConstants.JWT_TOKEN);
        this.removeCookie(cookieConstants.REMEMBER_ME);
        this.removeCookie(cookieConstants.SESSION_TIMEOUT);
        this.removeCookie(cookieConstants.PHPSESSID);
        localStorage.removeItem(storageConstants.ADRUM_CLIENTINFO);
        this.cookieService.set(cookieConstants.TOKEN, access_token, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TA_AUTH, access_token, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.SESSION_TTL, ttlString, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TOKEN_EXPIRATION, expires_in, this.sessionExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TOKEN_START_TIME, currentTime, this.sessionExpirationDate, path, domain);
    };
    /**
     * Stores agency info based cookies
     * @param data Agency info response
     * @param fromDTA flag to indicate whether or not is from DTA
     */
    TravelAgentService.prototype.storeAgencyInfo = function (data, fromDTA) {
        var cookieConstants = this.constants.COOKIES;
        var localeConfig = cookieConstants.LOCALE_COOKIE;
        var _a = cookieConstants.COOKIE_OPTIONS, path = _a.path, domain = _a.domain;
        var headerInfo = getValue(data, 'agencies.agency.agencyHeader');
        var addressInfo = getValue(data, 'agencies.agency.agencyAddresses.address[0]');
        var phoneInfo = getValue(data, 'agencies.agency.phones.phone[0]');
        var agencyId = headerInfo.agencyID;
        var guestSwid = this.cookieService.get(cookieConstants.CONVERSATION_UUID) + "_" + headerInfo.agencyID;
        var sessionGuid = data.msgHeader.sessionGuid;
        var defaultCurrency = !!localeConfig.currency[headerInfo.defaultCurrency] ?
            headerInfo.defaultCurrency :
            localeConfig.defaultCurrency;
        var currencyConfig = localeConfig.currency[defaultCurrency];
        var taInfo = {
            id: agencyId,
            agencyName: headerInfo.name,
            zipCode: addressInfo.zip,
            country: addressInfo.country,
            city: addressInfo.city,
            addressLine1: addressInfo.address,
            addressLine2: '',
            state: addressInfo.state,
            phone: phoneInfo.phoneNumber,
            partnerIdType: this.constants.AFFILIATION_LABEL,
            agentId: data.agentId,
            lastName: data.agentLastName,
            firstName: data.agentFirstName,
            email: data.agentEmail,
            partnerId: headerInfo.clia || headerInfo.arc || headerInfo.iata,
            returnUrl: fromDTA ? this.getDTAReferrer() : '',
            defaultCurrency: defaultCurrency
        };
        var taInfoString = JSON.stringify(taInfo);
        var localeCookie = {
            contentLocale: currencyConfig.contentLocale,
            preferredRegion: currencyConfig.preferredRegion,
            localeCurrency: defaultCurrency,
            precedence: localeConfig.precedence,
            version: localeConfig.version
        };
        var localeCookieString = JSON.stringify(localeCookie);
        var localeCookieExpiration = new Date();
        localeCookieExpiration.setDate(localeCookieExpiration.getDate() + localeConfig.expireDays);
        if (agencyId !== this.cookieService.get(cookieConstants.AGENCY_ID)) {
            this.removeCookie(cookieConstants.CART_ID_MAPPING);
            localStorage.removeItem(this.constants.STORAGE.ACTIVE_SESSION);
            localStorage.removeItem(this.constants.STORAGE.RECENT_RESERVATIONS);
            sessionStorage.removeItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO);
        }
        this.cookieService.set(cookieConstants.TA_SWID, guestSwid, this.oneDayExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.AGENCY_ID, agencyId, this.oneDayExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.TICKET_DATA, taInfoString, this.oneDayExpirationDate, path, domain);
        this.cookieService.set(cookieConstants.SESSION_GUID, sessionGuid, this.sessionExpirationDate, path, domain);
        this.cookieService.set(localeConfig.cookieName, localeCookieString, localeCookieExpiration, path);
    };
    /**
     * Stores SESSION_GUID cookie
     * @param data Agency info response
     */
    TravelAgentService.prototype.storeSessionGuid = function (data) {
        var cookieConstants = this.constants.COOKIES;
        var _a = cookieConstants.COOKIE_OPTIONS, path = _a.path, domain = _a.domain;
        var sessionGuid = data && data.msgHeader && data.msgHeader.sessionGuid;
        this.cookieService.delete(cookieConstants.SESSION_GUID, path, domain);
        this.cookieService.set(cookieConstants.SESSION_GUID, sessionGuid, this.sessionExpirationDate, path, domain);
    };
    /**
     * Stores agency passport cookie
     * @param data passport token response
     * @param { string } data.token passport token
     */
    TravelAgentService.prototype.storeAgencyPassportToken = function (_a) {
        var token = _a.token;
        var _b = this.constants.COOKIES.COOKIE_OPTIONS, path = _b.path, domain = _b.domain;
        this.cookieService.set(this.constants.COOKIES.TA_INFO, token, this.oneDayExpirationDate, path, domain);
    };
    /**
     * Retrieve passport token request payload based on agency information
     * @param agencyInfo agency info response
     * @returns agency passport token request data
     */
    TravelAgentService.prototype.getPassportTokenRequestData = function (agencyInfo) {
        var agencyHeader = getValue(agencyInfo, 'agencies.agency.agencyHeader');
        var phoneNumber = getValue(agencyInfo, 'agencies.agency.phones.phone[0].phoneNumber');
        var affiliationId = agencyHeader && (agencyHeader.clia || agencyHeader.arc || agencyHeader.iata);
        return {
            phoneNumber: phoneNumber,
            affiliationId: affiliationId,
            reservationId: this.constants.DEFAULT_RESERVATION_ID
        };
    };
    /**
     * Clear all travel agent data
     */
    TravelAgentService.prototype.clearTravelAgentData = function () {
        var cookieConstants = this.constants.COOKIES;
        var _a = cookieConstants.COOKIE_OPTIONS, path = _a.path, domain = _a.domain;
        this.cookieService.delete(cookieConstants.TOKEN, path, domain);
        this.cookieService.delete(cookieConstants.TOKEN_EXPIRATION, path, domain);
        this.cookieService.delete(cookieConstants.TOKEN_START_TIME, path, domain);
        this.cookieService.delete(cookieConstants.SESSION_TTL, path, domain);
        this.cookieService.delete(cookieConstants.TA_AUTH, path, domain);
        this.cookieService.delete(cookieConstants.AGENCY_ID, path, domain);
        this.cookieService.delete(cookieConstants.SESSION_GUID, path, domain);
        this.cookieService.delete(cookieConstants.TICKET_DATA, path, domain);
        this.cookieService.delete(cookieConstants.TA_SWID, path, domain);
        this.cookieService.delete(cookieConstants.TA_INFO, path, domain);
        this.cookieService.delete(cookieConstants.LOCALE_COOKIE.cookieName, path);
        localStorage.removeItem(this.constants.STORAGE.RECENT_RESERVATIONS);
        localStorage.removeItem(this.constants.STORAGE.ACTIVE_SESSION);
        sessionStorage.removeItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO);
    };
    /**
     * Clear DTA referrer from storage
     */
    TravelAgentService.prototype.removeDTAReferrer = function () {
        localStorage.removeItem(this.constants.STORAGE.DTA_REFERRER);
    };
    /**
     * Return DTA referrer from storage
     * @returns DTA referrer
     */
    TravelAgentService.prototype.getDTAReferrer = function () {
        return localStorage.getItem(this.constants.STORAGE.DTA_REFERRER);
    };
    /**
     * Store DTA referrer in local storage
     */
    TravelAgentService.prototype.storeDTAReferrer = function () {
        if (!this.getDTAReferrer()) {
            localStorage.setItem(this.constants.STORAGE.DTA_REFERRER, document.referrer);
        }
    };
    /**
     * Method to redirect to DTA site
     */
    TravelAgentService.prototype.redirectToDTAReferrer = function () {
        var referrer = this.getDTAReferrer();
        this.removeDTAReferrer();
        this.windowRef.nativeWindow.location.href = referrer;
    };
    /**
     * Login based on agency data
     * @param data agency login data
     * @param data.phoneNumber agency phone number
     * @param data.affiliationId agency affiliation id
     * @returns Observable for agency login process
     */
    TravelAgentService.prototype.agencyLogin = function (_a) {
        var _this = this;
        var phoneNumber = _a.phoneNumber, affiliationId = _a.affiliationId;
        return this.travelAgentRestService.getAuthToken()
            .pipe(tap(function (resp) { return _this.storeAuthToken(resp); }), concatMap(function () {
            return forkJoin([
                _this.travelAgentRestService.agencyLookup({ phoneNumber: phoneNumber, affiliationId: affiliationId })
                    .pipe(tap(function (resp) { return _this.storeAgencyInfo(resp); })),
                _this.travelAgentRestService.getPassportToken({
                    phoneNumber: phoneNumber,
                    affiliationId: affiliationId,
                    reservationId: _this.constants.DEFAULT_RESERVATION_ID
                })
                    .pipe(tap(function (resp) { return _this.storeAgencyPassportToken(resp); }))
            ]);
        }), catchError(function (error) {
            _this.clearTravelAgentData();
            return _throw(error);
        }), concatMap(function (resp) { return empty(); }));
    };
    /**
     * Login based on DTA token
     * @param tokens DTA tokens
     * @returns Observable for agency login process
     */
    TravelAgentService.prototype.dtaLogin = function (_a) {
        var _this = this;
        var tokens = _a.tokens;
        this.storeDTAReferrer();
        return this.travelAgentRestService.getAuthToken()
            .pipe(tap(function (resp) { return _this.storeAuthToken(resp); }), concatMap(function () { return _this.travelAgentRestService.agencyLookup({ tokens: tokens }); }), tap(function (resp) { return _this.storeAgencyInfo(resp, true); }), concatMap(function (resp) {
            return _this.travelAgentRestService.getPassportToken(_this.getPassportTokenRequestData(resp));
        }), tap(function (resp) { return _this.storeAgencyPassportToken(resp); }), catchError(function (error) {
            _this.clearTravelAgentData();
            return _throw(error);
        }), concatMap(function (resp) { return empty(); }));
    };
    /**
     * Delete sessions and agency data
     * @returns Observable for agency login process
     */
    TravelAgentService.prototype.logout = function () {
        var _this = this;
        var sessions;
        var observable;
        try {
            sessions = JSON.parse(localStorage.getItem(this.constants.STORAGE.ACTIVE_SESSION));
        }
        catch (error) {
            sessions = null;
        }
        observable = sessions ? this.travelAgentRestService.agencyLogout({ sessions: sessions }) : empty();
        return observable.pipe(finalize(function () { return _this.clearTravelAgentData(); }));
    };
    /**
     * Stores guest castaway info in session storage
     * @param data castaway info
     */
    TravelAgentService.prototype.storeGuestCastawayData = function (data) {
        try {
            sessionStorage.setItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO, encodeURIComponent(JSON.stringify(data)));
        }
        catch (error) { }
    };
    /**
     * Retrieve stored guest castaway data
     * @return guest castaway data from session storage
     */
    TravelAgentService.prototype.getGuestCastawayData = function () {
        var guestData;
        try {
            guestData = JSON.parse(decodeURIComponent(sessionStorage.getItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO)));
        }
        catch (error) { }
        return guestData;
    };
    /**
     * Deletes stored guest castaway data
     */
    TravelAgentService.prototype.deleteGuestCastawayData = function () {
        sessionStorage.removeItem(this.constants.STORAGE.GUEST_CASTAWAY_INFO);
    };
    return TravelAgentService;
}());
export { TravelAgentService };
